import { useNotifications, Notification } from '../contexts/NotificationsContext';
import { Bell, Check, Trash2, ExternalLink, AlertTriangle, Info, CheckCircle, XCircle } from 'lucide-react';
import { formatDate } from '../services/samApi';

interface NotificationsPanelProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function NotificationsPanel({ isOpen, onClose: _ }: NotificationsPanelProps) {
    const { notifications, markAsRead, markAllAsRead, deleteNotification } = useNotifications();

    if (!isOpen) return null;

    const getIcon = (type: Notification['type']) => {
        switch (type) {
            case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
            case 'success': return <CheckCircle className="w-5 h-5 text-green-500" />;
            case 'error': return <XCircle className="w-5 h-5 text-red-500" />;
            default: return <Info className="w-5 h-5 text-blue-500" />;
        }
    };

    return (
        <div
            className="w-full max-h-[80vh] bg-secondary/95 backdrop-blur-xl rounded-xl shadow-2xl border border-white/10 overflow-hidden flex flex-col"
        >
            {/* Header */}
            <div className="p-4 border-b border-white/10 flex justify-between items-center bg-white/5">
                <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4 text-primary" />
                    <h3 className="font-semibold text-white">Notifications</h3>
                    {notifications.some(n => !n.read) && (
                        <span className="bg-blue-500/20 text-blue-400 text-xs px-2 py-0.5 rounded-full font-medium">
                            {notifications.filter(n => !n.read).length} New
                        </span>
                    )}
                </div>
                {notifications.length > 0 && (
                    <button
                        onClick={markAllAsRead}
                        className="text-xs text-secondary hover:text-white transition-colors flex items-center gap-1"
                    >
                        <Check className="w-3 h-3" /> Mark all read
                    </button>
                )}
            </div>

            {/* List */}
            <div className="overflow-y-auto flex-1 custom-scrollbar">
                {notifications.length === 0 ? (
                    <div className="p-8 text-center text-tertiary">
                        <Bell className="w-12 h-12 mx-auto mb-3 opacity-20" />
                        <p>No notifications yet</p>
                    </div>
                ) : (
                    <div className="divide-y divide-white/5">
                        {notifications.map((notif) => (
                            <div
                                key={notif.id}
                                className={`p-4 hover:bg-white/5 transition-colors relative group ${!notif.read ? 'bg-blue-500/5' : ''}`}
                            >
                                <div className="flex gap-4">
                                    <div className="mt-1 flex-shrink-0">
                                        {getIcon(notif.type)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start mb-1">
                                            <h4 className={`text-sm font-medium ${!notif.read ? 'text-white' : 'text-secondary'}`}>
                                                {notif.title}
                                            </h4>
                                            <span className="text-xs text-tertiary whitespace-nowrap ml-2">
                                                {formatDate(notif.date).split(',')[0]}
                                            </span>
                                        </div>
                                        <p className="text-sm text-tertiary line-clamp-2 md:line-clamp-none">
                                            {notif.message}
                                        </p>

                                        <div className="flex items-center gap-4 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                            {notif.link && (
                                                <a
                                                    href={notif.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-xs flex items-center gap-1 text-accent hover:text-accent-hover"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <ExternalLink className="w-3 h-3" /> View
                                                </a>
                                            )}
                                            {!notif.read && (
                                                <button
                                                    onClick={() => markAsRead(notif.id)}
                                                    className="text-xs text-secondary hover:text-white"
                                                >
                                                    Mark read
                                                </button>
                                            )}
                                            <button
                                                onClick={() => deleteNotification(notif.id)}
                                                className="text-xs text-red-400 hover:text-red-300 ml-auto flex items-center gap-1"
                                            >
                                                <Trash2 className="w-3 h-3" /> Dismiss
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                {!notif.read && (
                                    <div className="absolute top-4 right-4 w-2 h-2 bg-blue-500 rounded-full" />
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
